import {pointerUtils, type CreateExtArgs, type DAL, type EnvironmentContext} from '@wix/document-manager-core'
import type {FragmentPointer} from '@wix/document-services-types'
import {ReportableError, taskWithRetries} from '@wix/document-manager-utils'
import {buildSiteContext, type SiteContext} from './index'
import type {FragmentsExtensionAPI} from '../../fragments/fragments'
import {RESOURCE_TYPES} from '../../../constants/constants'

const {getFragmentPointer} = pointerUtils

const SITE_CONTEXT_BUILD_ERROR_TYPE = 'proactiveAi_siteContext_buildError'
const SITE_CONTEXT_SINGLETON = 'SiteContextSingleton'
const SITE_CONTEXT_DATA_ID = 'siteContextData'

export interface GetSiteContextOptions {
    forceRebuild?: boolean
}

export interface SiteContextManager {
    getSiteContext(options?: GetSiteContextOptions): Promise<SiteContext>
}

interface CreateSiteContextManagerParams {
    dal: DAL
    fragments: FragmentsExtensionAPI['fragments']
    environmentContext: EnvironmentContext
    createExtArgs: CreateExtArgs
}

const getSiteContextPointer = (): FragmentPointer => {
    return getFragmentPointer({
        fragmentType: RESOURCE_TYPES.SITE_CONTEXT,
        fragmentInstance: SITE_CONTEXT_SINGLETON,
        type: 'data',
        id: SITE_CONTEXT_DATA_ID
    })
}

export const createSiteContextManager = ({
    dal,
    fragments,
    environmentContext,
    createExtArgs
}: CreateSiteContextManagerParams): SiteContextManager => {
    let buildPromise: Promise<SiteContext> | null = null

    const storeSiteContext = (siteContext: SiteContext): void => {
        fragments.addFragmentIndex({
            type: RESOURCE_TYPES.SITE_CONTEXT,
            name: SITE_CONTEXT_SINGLETON
        })
        fragments.add(
            {
                id: SITE_CONTEXT_DATA_ID,
                type: 'SiteContextData',
                brand: siteContext.brand,
                businessContext: siteContext.businessContext
            },
            'data',
            RESOURCE_TYPES.SITE_CONTEXT,
            SITE_CONTEXT_SINGLETON
        )
    }

    const buildAndStoreSiteContext = async (): Promise<SiteContext> => {
        try {
            const newContext = await taskWithRetries(
                () => buildSiteContext(environmentContext, createExtArgs),
                () => true,
                1
            )
            storeSiteContext(newContext)
            return newContext
        } catch (error) {
            const message = error instanceof Error ? error.message : String(error)
            throw new ReportableError({
                errorType: SITE_CONTEXT_BUILD_ERROR_TYPE,
                message: `Failed to build site context: ${message}`
            })
        }
    }

    const getSiteContext = async (options?: GetSiteContextOptions): Promise<SiteContext> => {
        const {forceRebuild = false} = options ?? {}

        if (!buildPromise) {
            if (!forceRebuild) {
                const storedContext = dal.get(getSiteContextPointer())
                if (storedContext) {
                    return storedContext
                }
            }
            buildPromise = buildAndStoreSiteContext().finally(() => {
                buildPromise = null
            })
        }
        return buildPromise
    }

    return {
        getSiteContext
    }
}
